import * as Haptics from "expo-haptics";
import { LinearGradient } from "expo-linear-gradient";
import { createContext, createElement, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Easing,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  TextStyle,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native";

import { useReducedMotion } from "../hooks/useReducedMotion";
import { supportsNativeDriver } from "../lib/animation";
import { isCentralHost } from "../lib/hosting";
import { central, motion, radii, semantic, shadows, spacing } from "../theme/tokens";

const CentralSurfaceContext = createContext(false);

/** Apply Central primitives on public routes without depending on the hostname. */
export function CentralSurface({ children }: PropsWithChildren) {
  return <CentralSurfaceContext.Provider value>{children}</CentralSurfaceContext.Provider>;
}

function useCentralSurface() {
  const explicitCentral = useContext(CentralSurfaceContext);
  return explicitCentral || isCentralHost();
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "link";
type BadgeTone = "gold" | "forest" | "cedar" | "mist";
type StatAccent = "forest" | "gold" | "cedar" | "ink";
type StatusTone = "success" | "warning" | "error" | "info" | "neutral";

type StateAction = {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
};

export function Panel({
  children,
  style,
  spotlight = false,
}: PropsWithChildren<{ style?: StyleProp<ViewStyle>; spotlight?: boolean }>) {
  const centralHost = useCentralSurface();
  const { reduceMotion } = useReducedMotion();
  const animatePanelEntrance = !centralHost && !reduceMotion;
  const panelScale = useRef(new Animated.Value(animatePanelEntrance ? motion.scaleFrom : 1)).current;
  const panelOpacity = useRef(new Animated.Value(animatePanelEntrance ? 0 : 1)).current;
  const flattenedStyle = StyleSheet.flatten(style);
  const wrapperLayoutStyle = flattenedStyle
    ? {
        alignSelf: flattenedStyle.alignSelf,
        flex: flattenedStyle.flex,
        flexBasis: flattenedStyle.flexBasis,
        flexGrow: flattenedStyle.flexGrow,
        flexShrink: flattenedStyle.flexShrink,
        maxWidth: flattenedStyle.maxWidth,
        minWidth: flattenedStyle.minWidth,
        width: flattenedStyle.width,
      }
    : null;

  useEffect(() => {
    if (!animatePanelEntrance) {
      panelScale.setValue(1);
      panelOpacity.setValue(1);
      return;
    }

    panelScale.setValue(motion.scaleFrom);
    panelOpacity.setValue(0);

    const entrance = Animated.parallel([
      Animated.timing(panelScale, {
        toValue: 1,
        duration: motion.entranceDuration,
        easing: Easing.bezier(...motion.easing),
        useNativeDriver: supportsNativeDriver,
      }),
      Animated.timing(panelOpacity, {
        toValue: 1,
        duration: motion.entranceDuration,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: supportsNativeDriver,
      }),
    ]);

    entrance.start();
    return () => entrance.stop();
  }, [animatePanelEntrance, panelOpacity, panelScale]);

  const content = spotlight && !centralHost ? (
    <LinearGradient
      colors={[semantic.surface.raised, semantic.surface.canvasAlt]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={StyleSheet.flatten([styles.panel, centralHost ? styles.centralPanel : null, style])}
    >
      {children}
    </LinearGradient>
  ) : (
    <View style={[styles.panel, centralHost ? styles.centralPanel : null, style]}>{children}</View>
  );

  if (!animatePanelEntrance) {
    return <View style={wrapperLayoutStyle}>{content}</View>;
  }

  return (
    <Animated.View
      style={[
        wrapperLayoutStyle,
        { opacity: panelOpacity },
        { transform: [{ scale: panelScale }] },
      ]}
    >
      {content}
    </Animated.View>
  );
}

export function Button({
  label,
  onPress,
  variant = "primary",
  brand = "default",
  compact = false,
  disabled = false,
  loading = false,
  fullWidth = false,
  shape = "pill",
  accessibilityHint,
  disabledHint,
  testID,
}: {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  brand?: "default" | "monarch";
  compact?: boolean;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  shape?: "pill" | "rounded";
  accessibilityHint?: string;
  disabledHint?: string;
  testID?: string;
}) {
  const centralHost = useCentralSurface();
  const monarchBrand = brand === "monarch";
  const { reduceMotion } = useReducedMotion();
  const [focused, setFocused] = useState(false);
  const scale = useRef(new Animated.Value(1)).current;
  const isDisabled = disabled || loading;

  useEffect(() => () => scale.stopAnimation(), [scale]);

  const animateScale = (toValue: number) => {
    if (reduceMotion) {
      scale.setValue(1);
      return;
    }

    scale.stopAnimation();
    Animated.timing(scale, {
      toValue,
      duration: motion.feedbackDuration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: supportsNativeDriver,
    }).start();
  };

  const handlePressIn = () => {
    if (isDisabled) return;
    if (!reduceMotion) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => undefined);
    }
    animateScale(motion.pressScale);
  };

  return (
    <Animated.View
      style={[
        styles.buttonWrap,
        fullWidth ? styles.buttonWrapFullWidth : null,
        reduceMotion ? null : { transform: [{ scale }] },
      ]}
    >
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={loading ? `${label}, loading` : label}
        accessibilityHint={accessibilityHint ?? (isDisabled ? disabledHint : undefined)}
        accessibilityState={{ disabled: isDisabled, busy: loading }}
        disabled={isDisabled}
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        onPress={(event) => {
          event.stopPropagation();
          onPress?.();
        }}
        onPressIn={handlePressIn}
        onPressOut={() => animateScale(1)}
        style={({ pressed }) => [
          styles.button,
          centralHost ? styles.centralButton : null,
          shape === "rounded" ? styles.buttonRounded : null,
          compact ? styles.buttonCompact : null,
          fullWidth ? styles.buttonFullWidth : null,
          variant === "secondary" ? styles.buttonSecondary : null,
          variant === "ghost" ? styles.buttonGhost : null,
          variant === "link" ? styles.buttonLink : null,
          monarchBrand ? styles.monarchButton : null,
          monarchBrand && variant === "secondary" ? styles.monarchButtonSecondary : null,
          monarchBrand && variant === "ghost" ? styles.monarchButtonGhost : null,
          centralHost && variant !== "primary" && variant !== "link" ? styles.centralButtonSubtle : null,
          focused ? styles.controlFocus : null,
          focused && centralHost ? styles.centralControlFocus : null,
          focused && variant === "link" ? styles.buttonLinkFocused : null,
          pressed && !isDisabled
            ? variant === "primary"
              ? monarchBrand ? styles.monarchButtonPressed : styles.buttonPressed
              : styles.buttonSubtlePressed
            : null,
          pressed && !isDisabled && centralHost
            ? variant === "primary"
              ? styles.centralButtonPressed
              : styles.centralButtonSubtlePressed
            : null,
          isDisabled ? styles.buttonDisabled : null,
          loading ? styles.buttonLoading : null,
        ]}
      >
        {loading ? (
          <ActivityIndicator
            accessibilityLabel="Loading"
            color={monarchBrand ? (variant === "primary" ? central.text.primary : central.action.primary) : variant === "primary" ? (centralHost ? central.text.inverse : semantic.text.inverse) : (centralHost ? central.text.primary : semantic.text.primary)}
            size="small"
          />
        ) : (
          <Text
            style={[
              styles.buttonLabel,
              monarchBrand ? styles.monarchButtonLabel : null,
              centralHost ? styles.centralButtonLabel : null,
              variant === "secondary" ? styles.buttonLabelSecondary : null,
              monarchBrand && variant !== "primary" ? styles.monarchButtonLabelSecondary : null,
              variant === "ghost" ? styles.buttonLabelGhost : null,
              variant === "link" ? styles.buttonLabelLink : null,
              centralHost && variant !== "primary" ? styles.centralButtonLabelSubtle : null,
              centralHost && variant === "link" ? styles.centralButtonLabelLink : null,
            ]}
          >
            {label}
          </Text>
        )}
      </Pressable>
      {isDisabled && disabledHint ? <Text accessibilityLiveRegion="polite" style={[styles.buttonDisabledHint, centralHost ? styles.centralButtonDisabledHint : null]}>{disabledHint}</Text> : null}
    </Animated.View>
  );
}

export function Field({
  label,
  hint,
  error,
  required = false,
  inputType,
  containerStyle,
  inputStyle,
  accessibilityLabel,
  accessibilityHint,
  brand = "default",
  ...inputProps
}: Omit<TextInputProps, "style" | "accessibilityLabel" | "accessibilityHint"> & {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  inputType?: "date" | "datetime-local" | "time";
  containerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  brand?: "default" | "monarch";
}) {
  const centralHost = useCentralSurface();
  const monarchBrand = brand === "monarch";
  const [focused, setFocused] = useState(false);
  const helperText = error ?? hint;

  return (
    <View style={[styles.field, containerStyle]}>
      <Text style={[styles.fieldLabel, monarchBrand ? styles.monarchFieldLabel : null]}>
        {label}
        {required ? <Text style={styles.requiredMark}> *</Text> : null}
      </Text>
      {Platform.OS === "web" && inputType
        ? createElement("input", {
            "aria-label": accessibilityLabel ?? label,
            "aria-required": required,
            disabled: inputProps.editable === false,
            lang: "en-US",
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
              setFocused(false);
              inputProps.onBlur?.(event as never);
            },
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              inputProps.onChangeText?.(event.currentTarget.value);
            },
            onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
              setFocused(true);
              inputProps.onFocus?.(event as never);
            },
            placeholder: inputProps.placeholder,
            required,
            step: inputType === "date" ? undefined : 60,
            style: {
              backgroundColor: centralHost || monarchBrand
                ? central.surface.raised
                : semantic.surface.raised,
              borderColor: error
                ? semantic.status.error.foreground
                : focused
                  ? centralHost
                    ? central.action.primary
                    : monarchBrand
                      ? "#DC923D"
                      : semantic.action.focus
                  : centralHost
                    ? central.border.default
                    : monarchBrand
                      ? "#C2CDDC"
                      : semantic.border.default,
              borderRadius: centralHost
                ? central.radius.control
                : monarchBrand
                  ? 10
                  : radii.control,
              borderStyle: "solid",
              borderWidth: focused ? 2 : 1,
              boxSizing: "border-box",
              color: centralHost || monarchBrand ? "#14233D" : semantic.text.primary,
              fontFamily: "Manrope_500Medium",
              fontSize: 15,
              lineHeight: "22px",
              minHeight: centralHost ? 50 : 48,
              outline: "none",
              padding: `${spacing.sm}px ${spacing.md}px`,
              width: "100%",
            },
            type: inputType,
            value: inputProps.value ?? "",
          })
        : <TextInput
            {...inputProps}
            accessibilityLabel={accessibilityLabel ?? label}
            accessibilityHint={
              accessibilityHint ?? helperText ?? (required ? "Required" : undefined)
            }
            accessibilityState={{ disabled: inputProps.editable === false }}
            onBlur={(event) => {
              setFocused(false);
              inputProps.onBlur?.(event);
            }}
            onFocus={(event) => {
              setFocused(true);
              inputProps.onFocus?.(event);
            }}
            placeholderTextColor={semantic.text.placeholder}
            style={[
              styles.fieldInput,
              monarchBrand ? styles.monarchFieldInput : null,
              centralHost ? styles.centralFieldInput : null,
              focused ? styles.fieldInputFocused : null,
              focused && monarchBrand ? styles.monarchFieldInputFocused : null,
              focused && centralHost ? styles.centralFieldInputFocused : null,
              error ? styles.fieldInputError : null,
              inputProps.multiline ? styles.fieldInputMultiline : null,
              inputStyle,
            ]}
          />}
      {helperText ? (
        <Text
          accessibilityLiveRegion={error ? "assertive" : "polite"}
          style={[styles.fieldHint, error ? styles.fieldError : null]}
        >
          {helperText}
        </Text>
      ) : null}
    </View>
  );
}

export function Badge({
  label,
  tone = "gold",
}: {
  label: string;
  tone?: BadgeTone;
}) {
  const toneMap: Record<BadgeTone, { backgroundColor: string; color: string }> = {
    gold: { backgroundColor: semantic.status.warning.background, color: semantic.status.warning.foreground },
    forest: { backgroundColor: semantic.status.success.background, color: semantic.status.success.foreground },
    cedar: { backgroundColor: semantic.surface.canvasAlt, color: semantic.text.muted },
    mist: { backgroundColor: semantic.status.info.background, color: semantic.status.info.foreground },
  };
  const centralTone = tone === "gold"
    ? central.status.warning
    : tone === "forest"
      ? central.status.success
      : tone === "cedar"
        ? central.status.neutral
        : central.status.info;
  const colors = useCentralSurface()
    ? { backgroundColor: centralTone.background, color: centralTone.foreground }
    : toneMap[tone];

  return (
    <View accessibilityRole="text" style={[styles.badge, { backgroundColor: colors.backgroundColor }]}>
      <Text style={[styles.badgeLabel, { color: colors.color }]}>{label}</Text>
    </View>
  );
}

export function Status({
  label,
  tone = "info",
}: {
  label: string;
  tone?: StatusTone;
}) {
  const colors = useCentralSurface() ? central.status[tone] : semantic.status[tone];

  return (
    <View
      accessible
      accessibilityRole="text"
      accessibilityLabel={`${tone}: ${label}`}
      style={[styles.status, { backgroundColor: colors.background }]}
    >
      <View style={[styles.statusDot, { backgroundColor: colors.foreground }]} />
      <Text style={[styles.statusLabel, { color: colors.foreground }]}>{label}</Text>
    </View>
  );
}

export function SegmentedPill({
  label,
  active,
  onPress,
  selectionRole = "button",
  brand = "default",
  disabled = false,
}: {
  label: string;
  active?: boolean;
  onPress?: () => void;
  selectionRole?: "button" | "radio";
  brand?: "default" | "monarch";
  disabled?: boolean;
}) {
  const centralHost = useCentralSurface();
  const monarchBrand = brand === "monarch";
  const { reduceMotion } = useReducedMotion();
  const [focused, setFocused] = useState(false);

  return (
    <Pressable
      accessibilityRole={selectionRole}
      accessibilityLabel={label}
      accessibilityState={selectionRole === "radio" ? { checked: active, disabled } : { selected: active, disabled }}
      disabled={disabled}
      onBlur={() => setFocused(false)}
      onFocus={() => setFocused(true)}
      onPress={() => {
        if (!reduceMotion) Haptics.selectionAsync().catch(() => undefined);
        onPress?.();
      }}
      style={({ pressed }) => [
        styles.segment,
        monarchBrand ? styles.monarchSegment : null,
        centralHost ? styles.centralSegment : null,
        active ? styles.segmentActive : null,
        active && monarchBrand ? styles.monarchSegmentActive : null,
        active && centralHost ? styles.centralSegmentActive : null,
        focused ? styles.controlFocus : null,
        focused && centralHost ? styles.centralControlFocus : null,
        pressed && !disabled ? styles.segmentPressed : null,
        disabled ? styles.segmentDisabled : null,
      ]}
    >
      <Text style={[styles.segmentLabel, monarchBrand ? styles.monarchSegmentLabel : null, active ? styles.segmentLabelActive : null, active && monarchBrand ? styles.monarchSegmentLabelActive : null, active && centralHost ? styles.centralSegmentLabelActive : null]}>{label}</Text>
    </Pressable>
  );
}

export function LoadingState({
  label = "Loading",
  description,
}: {
  label?: string;
  description?: string;
}) {
  const centralHost = useCentralSurface();
  return (
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={description ? `${label}. ${description}` : label}
      accessibilityLiveRegion="polite"
      nativeID="central-loading-state"
      style={[styles.state, centralHost ? styles.centralState : null]}
    >
      <ActivityIndicator color={centralHost ? central.action.primary : semantic.action.primary} size="small" />
      <View style={styles.stateCopy}>
        <Text style={[styles.stateTitle, centralHost ? styles.centralStateTitle : null]}>{label}</Text>
        {description ? <Text style={[styles.stateDescription, centralHost ? styles.centralStateDescription : null]}>{description}</Text> : null}
      </View>
    </View>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: StateAction;
}) {
  return <StateMessage tone="error" title={title} description={description} action={action} />;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: StateAction;
}) {
  return <StateMessage tone="info" title={title} description={description} action={action} />;
}

export function PermissionState({
  title = "You do not have access to this area",
  description = "Ask an administrator if you believe this is a mistake.",
  action,
  requiredRole,
}: {
  title?: string;
  description?: string;
  action?: StateAction;
  requiredRole?: string;
}) {
  const roleDescription = requiredRole ? `Required role: ${requiredRole}. ${description}` : description;
  return <StateMessage tone="warning" title={title} description={roleDescription} action={action} />;
}

function StateMessage({
  tone,
  title,
  description,
  action,
}: {
  tone: "error" | "warning" | "info";
  title: string;
  description?: string;
  action?: StateAction;
}) {
  const centralHost = useCentralSurface();
  const { width } = useWindowDimensions();
  const compact = width < 520;
  const colors = centralHost ? central.status[tone] : semantic.status[tone];
  const announcement = description ? `${title}. ${description}` : title;

  return (
    <View style={[styles.state, compact ? styles.stateCompact : null, centralHost ? styles.centralState : null, { backgroundColor: colors.background, borderColor: colors.foreground }]}>
      <View
        accessible
        accessibilityRole={tone === "error" ? "alert" : "text"}
        accessibilityLiveRegion={tone === "error" ? "assertive" : "polite"}
        accessibilityLabel={announcement}
        style={styles.stateCopy}
      >
        <Text style={[styles.stateTitle, { color: colors.foreground }]}>{title}</Text>
        {description ? <Text style={styles.stateDescription}>{description}</Text> : null}
      </View>
      {action ? <Button compact fullWidth={compact} label={action.label} onPress={action.onPress} variant={action.variant ?? "secondary"} /> : null}
    </View>
  );
}

export function Stat({
  value,
  label,
  accent = "forest",
  compact = false,
}: {
  value: string;
  label: string;
  accent?: StatAccent;
  compact?: boolean;
}) {
  const centralHost = useCentralSurface();
  const { reduceMotion } = useReducedMotion();
  const [displayValue, setDisplayValue] = useState(value);
  const opacity = useRef(new Animated.Value(reduceMotion ? 1 : 0)).current;
  const slide = useRef(new Animated.Value(reduceMotion ? 0 : motion.entranceOffset)).current;

  useEffect(() => {
    const numericTarget = parseInt(value, 10);
    if (reduceMotion || isNaN(numericTarget) || numericTarget <= 0) {
      setDisplayValue(value);
      return;
    }

    let frame = 0;
    const totalFrames = 16;
    const interval = setInterval(() => {
      frame += 1;
      const progress = frame / totalFrames;
      setDisplayValue(String(Math.round(numericTarget * (1 - Math.pow(1 - progress, 3)))));
      if (frame >= totalFrames) clearInterval(interval);
    }, motion.stateDuration / totalFrames);

    return () => clearInterval(interval);
  }, [reduceMotion, value]);

  useEffect(() => {
    if (reduceMotion) {
      opacity.setValue(1);
      slide.setValue(0);
      return;
    }

    opacity.setValue(0);
    slide.setValue(motion.entranceOffset);

    const entrance = Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: motion.entranceDuration,
        useNativeDriver: supportsNativeDriver,
      }),
      Animated.timing(slide, {
        toValue: 0,
        duration: motion.entranceDuration,
        easing: Easing.bezier(...motion.easing),
        useNativeDriver: supportsNativeDriver,
      }),
    ]);
    entrance.start();
    return () => entrance.stop();
  }, [opacity, reduceMotion, slide]);

  const accentColor: Record<StatAccent, string> = {
    forest: centralHost ? central.action.primary : semantic.action.primary,
    gold: centralHost ? central.accent.primary : semantic.status.warning.foreground,
    cedar: centralHost ? central.text.muted : semantic.text.muted,
    ink: centralHost ? central.text.primary : semantic.text.primary,
  };

  return (
    <Animated.View
      accessible
      accessibilityRole="text"
      accessibilityLabel={`${label}: ${displayValue}`}
      style={[
        styles.stat,
        centralHost ? styles.centralStat : null,
        compact ? styles.statCompact : null,
        { opacity },
        reduceMotion ? null : { transform: [{ translateY: slide }] },
      ]}
    >
      <Text style={[styles.statValue, centralHost ? styles.centralStatValue : null, { color: accentColor[accent] }, compact ? styles.statValueCompact : null]}>
        {displayValue}
      </Text>
      <Text style={styles.statLabel}>{label}</Text>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: semantic.surface.raised,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: semantic.border.default,
    padding: 22,
    minWidth: 0,
    ...shadows.soft,
  },
  centralPanel: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
    borderRadius: central.radius.card,
    padding: spacing.md,
    ...central.shadow,
  },
  buttonWrap: {
    alignSelf: "flex-start",
  },
  buttonWrapFullWidth: {
    alignSelf: "stretch",
    width: "100%",
  },
  button: {
    minHeight: 48,
    minWidth: 140,
    maxWidth: "100%",
    paddingHorizontal: 20,
    borderRadius: radii.pill,
    backgroundColor: semantic.action.primary,
    justifyContent: "center",
    alignItems: "center",
  },
  centralButton: {
    borderRadius: central.radius.control,
    backgroundColor: central.action.primary,
    minHeight: 50,
    minWidth: 0,
  },
  centralButtonSubtle: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.default,
    borderWidth: 1,
  },
  centralButtonLabel: {
    color: central.text.inverse,
  },
  centralButtonLabelLink: {
    color: central.action.primary,
    fontSize: 14,
    fontFamily: central.typography.bodyMedium,
  },
  centralButtonLabelSubtle: {
    color: central.text.primary,
  },
  buttonFullWidth: {
    width: "100%",
  },
  buttonRounded: {
    borderRadius: 8,
  },
  buttonCompact: {
    minHeight: 44,
    minWidth: 0,
    paddingHorizontal: 14,
  },
  buttonSecondary: {
    backgroundColor: semantic.action.secondary,
    borderWidth: 1,
    borderColor: semantic.border.default,
  },
  buttonGhost: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: semantic.border.default,
  },
  buttonLink: {
    backgroundColor: "transparent",
    borderWidth: 0,
    minHeight: 44,
    minWidth: 0,
    paddingHorizontal: spacing.xs,
  },
  buttonLinkFocused: {
    borderWidth: 0,
    shadowOpacity: 0,
  },
  buttonPressed: {
    backgroundColor: semantic.action.primaryPressed,
  },
  monarchButton: {
    backgroundColor: "#DC923D",
    borderRadius: 10,
  },
  monarchButtonSecondary: {
    backgroundColor: central.surface.raised,
    borderColor: "#10265E",
    borderWidth: 1,
  },
  monarchButtonGhost: {
    backgroundColor: "transparent",
    borderColor: "#10265E",
    borderWidth: 1,
  },
  monarchButtonPressed: {
    backgroundColor: "#C47C2F",
  },
  centralButtonPressed: {
    backgroundColor: central.action.primaryPressed,
  },
  buttonSubtlePressed: {
    backgroundColor: semantic.surface.subtle,
  },
  centralButtonSubtlePressed: {
    backgroundColor: central.surface.muted,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonLoading: {
    opacity: 0.82,
  },
  buttonDisabledHint: {
    color: semantic.text.muted,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
    marginTop: spacing.xs,
    maxWidth: 280,
  },
  centralButtonDisabledHint: {
    color: central.text.muted,
  },
  controlFocus: {
    borderColor: semantic.action.focus,
    borderWidth: 2,
    shadowColor: semantic.action.focus,
    shadowOpacity: 0.32,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
  },
  centralControlFocus: {
    borderColor: central.action.primary,
    shadowColor: central.action.primary,
  },
  buttonLabel: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
    flexShrink: 1,
    textAlign: "center",
  },
  monarchButtonLabel: {
    color: "#14233D",
    fontFamily: central.typography.label,
  },
  monarchButtonLabelSecondary: {
    color: "#10265E",
  },
  buttonLabelSecondary: {
    color: semantic.text.primary,
  },
  buttonLabelGhost: {
    color: semantic.text.primary,
  },
  buttonLabelLink: {
    color: semantic.text.link,
  },
  field: {
    gap: spacing.xs,
  },
  fieldLabel: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
    lineHeight: 18,
  },
  monarchFieldLabel: {
    color: "#14233D",
    fontFamily: central.typography.label,
  },
  requiredMark: {
    color: semantic.status.error.foreground,
  },
  fieldInput: {
    minHeight: 48,
    borderRadius: radii.control,
    borderWidth: 1,
    borderColor: semantic.border.default,
    backgroundColor: semantic.surface.raised,
    color: semantic.text.primary,
    fontFamily: "Manrope_500Medium",
    fontSize: 15,
    lineHeight: 22,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  monarchFieldInput: {
    backgroundColor: central.surface.raised,
    borderColor: "#C2CDDC",
    borderRadius: 10,
    color: "#14233D",
  },
  centralFieldInput: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.default,
    borderRadius: central.radius.control,
    minHeight: 50,
  },
  fieldInputFocused: {
    borderColor: semantic.action.focus,
    borderWidth: 2,
  },
  monarchFieldInputFocused: {
    borderColor: "#DC923D",
  },
  centralFieldInputFocused: {
    borderColor: central.action.primary,
  },
  fieldInputError: {
    borderColor: semantic.status.error.foreground,
  },
  fieldInputMultiline: {
    minHeight: 112,
    textAlignVertical: "top",
  },
  fieldHint: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
    lineHeight: 18,
  },
  fieldError: {
    color: semantic.status.error.foreground,
  },
  badge: {
    alignSelf: "flex-start",
    borderRadius: radii.pill,
    paddingHorizontal: spacing.sm,
    paddingVertical: 7,
  },
  badgeLabel: {
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 0.3,
  },
  status: {
    alignSelf: "flex-start",
    alignItems: "center",
    borderRadius: radii.pill,
    flexDirection: "row",
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusLabel: {
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 16,
  },
  segment: {
    minHeight: 44,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: semantic.border.default,
    backgroundColor: semantic.surface.raised,
    maxWidth: "100%",
    justifyContent: "center",
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  monarchSegment: {
    borderColor: "#C2CDDC",
    borderRadius: 10,
  },
  centralSegment: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.default,
    borderRadius: central.radius.control,
  },
  segmentActive: {
    backgroundColor: semantic.action.primary,
    borderColor: semantic.action.primary,
  },
  monarchSegmentActive: {
    backgroundColor: "#10265E",
    borderColor: "#10265E",
  },
  centralSegmentActive: {
    backgroundColor: central.action.primary,
    borderColor: central.action.primary,
  },
  segmentPressed: {
    opacity: 0.86,
  },
  segmentDisabled: {
    opacity: 0.48,
  },
  segmentLabel: {
    color: semantic.text.primary,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    flexShrink: 1,
    textAlign: "center",
  },
  monarchSegmentLabel: {
    color: "#14233D",
    fontFamily: central.typography.bodyMedium,
  },
  segmentLabelActive: {
    color: semantic.text.inverse,
  },
  monarchSegmentLabelActive: {
    color: central.text.inverse,
  },
  centralSegmentLabelActive: {
    color: central.text.inverse,
  },
  state: {
    alignItems: "center",
    backgroundColor: semantic.surface.subtle,
    borderColor: semantic.border.default,
    borderRadius: radii.md,
    borderWidth: 1,
    flexDirection: "row",
    gap: spacing.md,
    justifyContent: "space-between",
    padding: spacing.md,
  },
  stateCompact: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  centralState: {
    backgroundColor: central.status.info.background,
    borderColor: central.border.subtle,
    borderRadius: central.radius.control,
  },
  stateCopy: {
    flex: 1,
    gap: spacing.xxs,
    minWidth: 0,
  },
  stateTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
    lineHeight: 21,
  },
  stateDescription: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 19,
  },
  centralStateTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
  },
  centralStateDescription: {
    color: central.text.secondary,
    fontFamily: central.typography.body,
  },
  stat: {
    minWidth: 150,
    flexBasis: 150,
    flexGrow: 1,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: semantic.border.default,
    backgroundColor: semantic.surface.raised,
    padding: spacing.md,
    gap: 6,
  },
  centralStat: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
    borderRadius: central.radius.card,
    padding: spacing.sm,
  },
  statCompact: {
    minWidth: 118,
    flexBasis: 118,
    padding: 14,
  },
  statValue: {
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 38,
    lineHeight: 40,
  },
  centralStatValue: {
    fontFamily: central.typography.display,
    fontSize: 30,
  },
  statValueCompact: {
    fontSize: 30,
    lineHeight: 32,
  },
  statLabel: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 19,
    textTransform: "capitalize",
  },
});
